import { useState, type ReactNode } from "react";
import { BLOCK_CATALOG, type Block, type BlockStyle, type BlockType, type Breakpoint, type BreakpointOverrides } from "@alchimie/shared";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { COLORS } from "../ui.js";
import { createBlock } from "./defaults.js";
import { BlockRenderer } from "./BlockRenderer.js";
import { PropertyEditor } from "./PropertyEditor.js";
import { StyleEditor } from "./StyleEditor.js";

const BREAKPOINT_WIDTHS: Record<Breakpoint, number | undefined> = {
  desktop: undefined,
  tablet: 768,
  mobile: 390,
};

interface Props {
  blocks: Block[];
  onBlocksChange: (blocks: Block[]) => void;
  overrides: BreakpointOverrides;
  onOverridesChange: (overrides: BreakpointOverrides) => void;
  topbarLeft?: ReactNode;
  topbarTitle?: ReactNode;
  topbarRight?: ReactNode;
  /** Sous-ensemble de blocs autorisés. Par défaut tous. */
  allowedBlockTypes?: BlockType[];
  /** Si défini, expose un bouton 'Enregistrer' aux éditeurs profonds (ex : modal HTML). */
  onPersist?: () => void;
}

/**
 * Espace de travail Builder réutilisable.
 *
 * - Palette gauche : catalogue de blocs (filtrable via allowedBlockTypes).
 * - Canvas central : sortable via dnd-kit + boutons ↑↓✕.
 * - Panneau droit : Propriétés / Style avec overrides par breakpoint.
 * - Topbar : breakpoint toggle + preview + slots libres pour boutons custom.
 *
 * Utilisé par Builder (pages de vente) ET LessonEditor (contenu de leçon).
 */
export function BlocksWorkspace({
  blocks,
  onBlocksChange,
  overrides,
  onOverridesChange,
  topbarLeft,
  topbarTitle,
  topbarRight,
  allowedBlockTypes,
  onPersist,
}: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<"props" | "style">("props");
  const [breakpoint, setBreakpoint] = useState<Breakpoint>("desktop");
  const [preview, setPreview] = useState(false);
  // Drill-in dans une colonne : { colIdx, nestedId } actif tant que le bloc
  // parent COLUMNS reste sélectionné. Réinitialisé quand on change de bloc.
  const [drill, setDrill] = useState<{ colIdx: number; nestedId: string } | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const selected = blocks.find((b) => b.id === selectedId) ?? null;
  const selectedIdx = selected ? blocks.findIndex((b) => b.id === selected.id) : -1;

  function addBlock(type: BlockType) {
    const b = createBlock(type);
    onBlocksChange([...blocks, b]);
    setSelectedId(b.id);
    setDrill(null);
  }

  function selectBlock(id: string | null) {
    setSelectedId(id);
    setDrill(null);
    setTab("props");
  }

  // Renvoie le bloc actuellement édité (parent ou imbriqué) + un writer qui
  // propage vers le tableau racine.
  function resolveEditing(): { block: Block; write: (b: Block) => void; nested: boolean } | null {
    if (!selected) return null;
    if (drill && selected.type === "COLUMNS") {
      const cols = selected.props.columns;
      const col = cols[drill.colIdx];
      const nested = col?.find((b) => b.id === drill.nestedId);
      if (nested) {
        return {
          block: nested,
          nested: true,
          write: (b) => {
            const newCols = cols.slice();
            newCols[drill.colIdx] = col.map((x) => (x.id === drill.nestedId ? b : x));
            updateBlock(selectedIdx, { ...selected, props: { columns: newCols } });
          },
        };
      }
      // Le bloc imbriqué a disparu — on sort du drill.
      setDrill(null);
    }
    return { block: selected, nested: false, write: (b) => updateBlock(selectedIdx, b) };
  }

  function moveBlock(idx: number, dir: -1 | 1) {
    const j = idx + dir;
    if (j < 0 || j >= blocks.length) return;
    const cp = blocks.slice();
    [cp[idx], cp[j]] = [cp[j], cp[idx]];
    onBlocksChange(cp);
  }

  function removeBlock(idx: number) {
    const removed = blocks[idx];
    onBlocksChange(blocks.filter((_, i) => i !== idx));
    if (selectedId === removed.id) {
      setSelectedId(null);
      setDrill(null);
    }
  }

  function updateBlock(idx: number, next: Block) {
    const cp = blocks.slice();
    cp[idx] = next;
    onBlocksChange(cp);
  }

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = blocks.findIndex((b) => b.id === active.id);
    const newIdx = blocks.findIndex((b) => b.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onBlocksChange(arrayMove(blocks, oldIdx, newIdx));
  }

  function editSelectedStyle(s: BlockStyle) {
    const r = resolveEditing();
    if (!r) return;
    if (breakpoint === "desktop") {
      r.write({ ...r.block, style: s });
    } else if (!r.nested) {
      // Les overrides de breakpoint ne s'appliquent qu'au top-level (le
      // mapping est par id racine). On garde ce comportement pour l'instant.
      const cur = overrides[r.block.id] ?? {};
      onOverridesChange({ ...overrides, [r.block.id]: { ...cur, [breakpoint]: s } });
    } else {
      // Pour les blocs imbriqués on écrit dans block.style directement
      // (pas d'override par breakpoint en V1).
      r.write({ ...r.block, style: s });
    }
  }

  function effectiveStyle(b: Block): BlockStyle | undefined {
    if (breakpoint === "desktop") return b.style;
    const o = overrides[b.id]?.[breakpoint];
    if (!o) return b.style;
    return { ...b.style, ...o };
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "grid",
        gridTemplateColumns: "240px 1fr 320px",
        gridTemplateRows: "56px 1fr",
        background: "#f5f3ef",
        fontFamily: "system-ui, sans-serif",
        color: COLORS.text,
      }}
    >
      {/* TOPBAR */}
      <div
        style={{
          gridColumn: "1 / 4",
          background: "#fff",
          borderBottom: `1px solid ${COLORS.border}`,
          display: "flex",
          alignItems: "center",
          padding: "0 16px",
          gap: 12,
        }}
      >
        {topbarLeft}
        <div style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{topbarTitle}</div>
        <BreakpointToggle value={breakpoint} onChange={setBreakpoint} />
        <button
          onClick={() => setPreview((p) => !p)}
          title={preview ? "Mode édition" : "Aperçu"}
          style={{
            padding: "6px 14px",
            border: `1px solid ${preview ? COLORS.accent : COLORS.border}`,
            background: preview ? COLORS.accent : "transparent",
            color: preview ? "#fff" : COLORS.text,
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            fontFamily: "inherit",
          }}
        >
          {preview ? "← Éditer" : "👁 Aperçu"}
        </button>
        {topbarRight}
      </div>

      {/* PALETTE */}
      <div
        style={{
          background: "#fff",
          borderRight: `1px solid ${COLORS.border}`,
          overflowY: "auto",
          padding: "16px 12px",
          display: preview ? "none" : "block",
        }}
      >
        {(["structure", "media", "marketing", "advanced"] as const).map((g) => {
          const items = (Object.entries(BLOCK_CATALOG) as [BlockType, (typeof BLOCK_CATALOG)[BlockType]][]).filter(
            ([type, info]) =>
              info.group === g && (!allowedBlockTypes || allowedBlockTypes.includes(type))
          );
          if (items.length === 0) return null;
          return (
            <div key={g}>
              <div style={paletteGroupTitle}>
                {g === "structure" ? "Structure" : g === "media" ? "Média" : g === "marketing" ? "Marketing" : "Avancé"}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 16 }}>
                {items.map(([type, info]) => (
                  <button
                    key={type}
                    onClick={() => addBlock(type)}
                    style={paletteBtn}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background = "#fff";
                      (e.currentTarget as HTMLButtonElement).style.borderColor = COLORS.accent;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background = "#fafaf8";
                      (e.currentTarget as HTMLButtonElement).style.borderColor = COLORS.border;
                    }}
                  >
                    <span style={{ fontSize: 16 }}>{info.icon}</span>
                    <span style={{ fontSize: 11, fontWeight: 500 }}>{info.label}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* CANVAS */}
      <div
        style={{
          overflowY: "auto",
          padding: 32,
          background: "#f5f3ef",
          gridColumn: preview ? "1 / 4" : undefined,
        }}
      >
        <div
          style={{
            background: "#fafaf8",
            minHeight: "calc(100vh - 56px - 64px)",
            borderRadius: 12,
            border: `1px solid ${COLORS.border}`,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            maxWidth: BREAKPOINT_WIDTHS[breakpoint],
            margin: BREAKPOINT_WIDTHS[breakpoint] ? "0 auto" : undefined,
            transition: "max-width 0.25s ease",
          }}
        >
          {blocks.length === 0 ? (
            <div style={{ padding: 80, textAlign: "center", color: COLORS.muted, fontStyle: "italic" }}>
              Canvas vide. Cliquez sur un type de bloc dans la palette pour démarrer.
            </div>
          ) : preview ? (
            blocks.map((b) => <BlockRenderer key={b.id} block={{ ...b, style: effectiveStyle(b) }} />)
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                {blocks.map((b, i) => (
                  <SortableCanvasBlock
                    key={b.id}
                    block={{ ...b, style: effectiveStyle(b) }}
                    selected={b.id === selectedId}
                    onSelect={() => selectBlock(b.id)}
                    onMoveUp={() => moveBlock(i, -1)}
                    onMoveDown={() => moveBlock(i, 1)}
                    onRemove={() => removeBlock(i)}
                    canMoveUp={i > 0}
                    canMoveDown={i < blocks.length - 1}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      {/* PROPERTIES */}
      <div
        style={{
          background: "#fff",
          borderLeft: `1px solid ${COLORS.border}`,
          overflowY: "auto",
          display: preview ? "none" : "block",
        }}
      >
        {selected ? (() => {
          const r = resolveEditing();
          if (!r) return null;
          const styleForEdit =
            !r.nested && breakpoint !== "desktop"
              ? overrides[r.block.id]?.[breakpoint] ?? {}
              : r.block.style;
          return (
          <div>
            {r.nested && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 12px",
                  background: "#f5f3ef",
                  borderBottom: `1px solid ${COLORS.border}`,
                  fontSize: 12,
                }}
              >
                <button
                  onClick={() => setDrill(null)}
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: COLORS.accent,
                    fontWeight: 600,
                    fontSize: 12,
                    fontFamily: "inherit",
                    padding: 0,
                  }}
                >
                  ← Retour aux Colonnes
                </button>
                <span style={{ color: COLORS.muted }}>/ {r.block.type}</span>
              </div>
            )}
            <div
              style={{
                display: "flex",
                borderBottom: `1px solid ${COLORS.border}`,
                position: "sticky",
                top: 0,
                background: "#fff",
                zIndex: 1,
              }}
            >
              <TabButton active={tab === "props"} onClick={() => setTab("props")}>Propriétés</TabButton>
              <TabButton active={tab === "style"} onClick={() => setTab("style")}>Style</TabButton>
            </div>
            <div style={{ padding: 16 }}>
              {tab === "props" ? (
                <PropertyEditor
                  block={r.block}
                  onChange={r.write}
                  onPersist={onPersist}
                  onEnterNested={(colIdx, nestedId) => {
                    setDrill({ colIdx, nestedId });
                    setTab("props");
                  }}
                />
              ) : (
                <div>
                  {!r.nested && breakpoint !== "desktop" && (
                    <div
                      style={{
                        padding: 10,
                        marginBottom: 16,
                        background: "#fef3c7",
                        border: "1px solid #fde68a",
                        borderRadius: 6,
                        fontSize: 12,
                        color: "#92400e",
                      }}
                    >
                      Édition en mode <strong>{breakpoint}</strong> — les modifs ne s'appliquent qu'à cette taille.
                    </div>
                  )}
                  <StyleEditor
                    style={styleForEdit}
                    onChange={editSelectedStyle}
                    blockType={r.block.type}
                  />
                </div>
              )}
            </div>
          </div>
          );
        })() : (
          <div style={{ padding: 32, color: COLORS.muted, fontStyle: "italic", textAlign: "center" }}>
            Sélectionnez un bloc dans le canvas pour éditer ses propriétés.
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
function SortableCanvasBlock({
  block,
  selected,
  onSelect,
  onMoveUp,
  onMoveDown,
  onRemove,
  canMoveUp,
  canMoveDown,
}: {
  block: Block;
  selected: boolean;
  onSelect: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: "relative",
    outline: selected ? `2px solid ${COLORS.accent}` : "2px solid transparent",
    outlineOffset: -2,
    cursor: "pointer",
    zIndex: isDragging ? 100 : 1,
    opacity: isDragging ? 0.85 : 1,
    boxShadow: isDragging ? "0 12px 32px rgba(0,0,0,0.18)" : "none",
  };
  return (
    <div ref={setNodeRef} style={style} onClick={onSelect}>
      {/* pointer-events:none sur le contenu pour que les clics sur des liens
          ou des éléments interactifs (ex : HTML Giri importé) sélectionnent
          le bloc au lieu d'être interceptés. Le preview mode bypass ce wrapper. */}
      <div style={{ pointerEvents: "none" }}>
        <BlockRenderer block={block} />
      </div>
      {selected && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            background: COLORS.accent,
            borderRadius: 6,
            padding: 4,
            display: "flex",
            gap: 2,
            zIndex: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          <DragHandle attributes={attributes} listeners={listeners} />
          <IconButton onClick={onMoveUp} disabled={!canMoveUp} title="Monter">↑</IconButton>
          <IconButton onClick={onMoveDown} disabled={!canMoveDown} title="Descendre">↓</IconButton>
          <IconButton onClick={onRemove} title="Supprimer">✕</IconButton>
        </div>
      )}
    </div>
  );
}

function DragHandle({ attributes, listeners }: { attributes: object; listeners: object | undefined }) {
  return (
    <button
      {...(attributes as React.HTMLAttributes<HTMLButtonElement>)}
      {...(listeners as React.HTMLAttributes<HTMLButtonElement>)}
      title="Déplacer"
      style={{
        width: 28,
        height: 28,
        border: "none",
        background: "transparent",
        color: "#fff",
        cursor: "grab",
        borderRadius: 4,
        fontSize: 13,
        fontFamily: "inherit",
        opacity: 0.85,
      }}
    >
      ⋮⋮
    </button>
  );
}

function IconButton({
  children,
  onClick,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        width: 28,
        height: 28,
        border: "none",
        background: "transparent",
        color: "#fff",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        borderRadius: 4,
        fontSize: 13,
        fontFamily: "inherit",
      }}
    >
      {children}
    </button>
  );
}

function BreakpointToggle({ value, onChange }: { value: Breakpoint; onChange: (b: Breakpoint) => void }) {
  const items: { bp: Breakpoint; icon: string }[] = [
    { bp: "desktop", icon: "🖥" },
    { bp: "tablet", icon: "▦" },
    { bp: "mobile", icon: "▯" },
  ];
  return (
    <div style={{ display: "flex", gap: 2, background: "#f5f3ef", borderRadius: 6, padding: 2 }}>
      {items.map(({ bp, icon }) => (
        <button
          key={bp}
          onClick={() => onChange(bp)}
          style={{
            padding: "6px 10px",
            border: "none",
            background: value === bp ? "#fff" : "transparent",
            color: COLORS.text,
            cursor: "pointer",
            borderRadius: 4,
            fontSize: 13,
            fontFamily: "inherit",
            boxShadow: value === bp ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
          }}
        >
          {icon}
        </button>
      ))}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: "12px",
        border: "none",
        background: "transparent",
        cursor: "pointer",
        fontSize: 13,
        fontWeight: active ? 700 : 500,
        color: active ? COLORS.accent : COLORS.muted,
        borderBottom: `2px solid ${active ? COLORS.accent : "transparent"}`,
        fontFamily: "inherit",
      }}
    >
      {children}
    </button>
  );
}

const paletteGroupTitle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: COLORS.muted,
  textTransform: "uppercase",
  letterSpacing: 0.8,
  margin: "8px 4px 8px",
};

const paletteBtn: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  padding: "10px 6px",
  background: "#fafaf8",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 11,
  fontFamily: "inherit",
  color: COLORS.text,
  transition: "all 0.1s",
};
